// POSProductsTable.js - Product table view for POS cashier
// Displays all products with: Image, Name, Price, Stock, and Quick Add to Cart button
import React, { useState, useMemo, useEffect } from "react";
import EmptyState from "../../CommonComponents/EmptyState";
import SearchInput from "../../CommonComponents/SearchInput";
import Pagination from "../../CommonComponents/Pagination";
import { Package, WifiOff } from "lucide-react";
import { usePOSProductsContext } from "../contexts/POSProductContext";
import ProductImageFallback from "../../CommonComponents/ProductImageFallback";
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from '../../../utils/tableStyle';

// Utility function to format price in PHP currency
const formatPrice = (amount) => {
    if (isNaN(amount)) return "₱0.00";
    return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
    }).format(amount);
};

// Helper component for product image
const ProductImage = ({ imageUrl, name, outOfStock }) => {
    const [src, setSrc] = React.useState(imageUrl || null);

    const handleImageError = (error) => {
        console.warn(`[ProductImage] Failed to load image for "${name}": ${imageUrl}`, error);
        setSrc(null);
    };

    return (
        <div className="relative h-10 w-10 flex-shrink-0">
            {src ? (
                <img
                    src={src}
                    alt={name}
                    className="h-10 w-10 object-cover rounded border border-gray-200"
                    onError={handleImageError}
                />
            ) : (
                <ProductImageFallback className="h-10 w-10 rounded" aria-label="No product image" />
            )}
            {outOfStock && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded z-0 pointer-events-none">
                    <span className="text-white text-[9px] font-bold">Out</span>
                </div>
            )}
        </div>
    );
};

function POSProductsTable() {
    // Get products from context
    const { products, loading, error, fetchProducts } = usePOSProductsContext();

    // Local state
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const productsPerPage = 10;

    // Filter products by search
    const filteredProducts = useMemo(() => {
        return products.filter((p) =>
            p.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [products, searchTerm]);
    const productSuggestions = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        if (!normalizedSearch) return [];
        const uniqueNames = new Set();

        return products
            .map((product) => product.name)
            .filter((name) => {
                if (!name || uniqueNames.has(name)) return false;
                const matches = name.toLowerCase().includes(normalizedSearch);
                if (matches) {
                    uniqueNames.add(name);
                }
                return matches;
            })
            .slice(0, 6);
    }, [products, searchTerm]);

    // Pagination logic
    const totalPages = Math.max(Math.ceil(filteredProducts.length / productsPerPage), 1);
    const currentProducts = filteredProducts.slice(
        (currentPage - 1) * productsPerPage,
        currentPage * productsPerPage
    );

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    return (
        <div className="pt-6">
            {/* Search Bar */}
            <div className="mb-4">
                <SearchInput
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    onSuggestionSelect={(productName) => {
                        setSearchTerm(productName);
                        setCurrentPage(1);
                    }}
                    suggestions={productSuggestions}
                    placeholder="Search by product name..."
                />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>Products List</h2>
            </div>

            {/* Table Container */}
            {error ? (
                <EmptyState
                    icon={WifiOff}
                    title="Failed to Load Products"
                    message="Check your internet connection and try again."
                    onRetry={fetchProducts}
                    retryLabel="Reload"
                />
            ) : loading ? (
                <div className="text-center py-8 text-gray-500">Loading products...</div>
            ) : !filteredProducts.length ? (
                <EmptyState
                    icon={Package}
                    title={searchTerm ? "No Products Found" : "No Products Available"}
                    message={searchTerm ? "Try a different search term." : "No products are currently available."}
                />
            ) : (
                <div className={tableContainer}>
                    <table className={tableBase}>
                        <thead className={tableHead} style={{ background: 'var(--color-primary)' }}>
                            <tr>
                                <th className={tableHeader + ' text-center'}>Image</th>
                                <th className={tableHeader + ' text-center'}>Product Name</th>
                                <th className={tableHeader + ' text-center'}>Code</th>
                                <th className={tableHeader + ' text-center'}>Description</th>
                                <th className={tableHeader + ' text-center'}>Price</th>
                                <th className={tableHeader + ' text-center'}>Branch Stock</th>
                                <th className={tableHeader + ' text-center'}>Warehouse Stock</th>
                                <th className={tableHeader + ' text-center'}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentProducts.map((product) => {
                                const isOutOfStock = product.stock === 0 || product.stock === null || product.stock === undefined;
                                const warehouseStock = product.warehouse_stock || 0;
                                const hasWarehouseStock = warehouseStock > 0;
                                const isLowWarehouseStock = warehouseStock > 0 && warehouseStock < 10;

                                return (
                                    <tr key={product.id} className={tableRow + ' border-b last:border-0'}>
                                        <td className={tableCell + ' text-center'}>
                                            <ProductImage
                                                imageUrl={product.image}
                                                name={product.name}
                                                outOfStock={isOutOfStock}
                                            />
                                        </td>
                                        <td className={tableCell + ' text-center'}>{product.name}</td>
                                        <td className={tableCell + ' text-center'}>{product.product_code || '-'}</td>
                                        <td className={tableCell + ' text-center text-xs'}>
                                            {product.description && product.description !== product.name ? product.description : '-'}
                                        </td>
                                        <td className={tableCell + ' text-center'}>{formatPrice(product.price)}</td>
                                        <td className={tableCell + ' text-center'}>
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${isOutOfStock
                                                ? 'bg-red-100 text-red-800'
                                                : product.stock < 10
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-green-100 text-green-800'
                                                }`}>
                                                {isOutOfStock ? 'Out' : product.stock} {!isOutOfStock && 'units'}
                                            </span>
                                        </td>
                                        <td className={tableCell + ' text-center'}>
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${!hasWarehouseStock
                                                ? 'bg-red-100 text-red-800'
                                                : isLowWarehouseStock
                                                    ? 'bg-orange-100 text-orange-800'
                                                    : 'bg-green-100 text-green-800'
                                                }`}>
                                                {warehouseStock} units
                                            </span>
                                        </td>
                                        <td className={tableCell + ' text-center'}>
                                            {!hasWarehouseStock ? (
                                                <span className='inline-flex items-center gap-1 bg-red-50 text-red-700 text-[11px] px-2 py-0.5 rounded-full font-medium'>
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
                                                    Out of Stock
                                                </span>
                                            ) : isLowWarehouseStock ? (
                                                <span className='inline-flex items-center gap-1 bg-orange-50 text-orange-700 text-[11px] px-2 py-0.5 rounded-full font-medium'>
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 8v8m0 4v.01M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" /></svg>
                                                    Low Stock
                                                </span>
                                            ) : (
                                                <span className='inline-flex items-center gap-1 bg-green-50 text-green-700 text-[11px] px-2 py-0.5 rounded-full font-medium'>
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" /></svg>
                                                    Available
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination - Only show when no error and data exists */}
            {!error && filteredProducts.length > 0 && (
                <div className="flex flex-col items-center gap-2">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                    <p className="text-sm" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
                        Showing{" "}
                        {filteredProducts.length === 0
                            ? 0
                            : (currentPage - 1) * productsPerPage + 1}{" "}
                        –{" "}
                        {Math.min(currentPage * productsPerPage, filteredProducts.length)}{" "}
                        of {filteredProducts.length} items | Page {currentPage} of {totalPages}
                    </p>
                </div>
            )}
        </div>
    );
}

export default POSProductsTable;
